import { AgCheckbox } from 'app/agriculture/components/form/AgCheckbox/AgCheckbox';
import { Button } from 'app/agriculture/components/Button/Button';
import { CheckIcon, EditIcon, MinusIcon, PlusIcon } from 'app/agriculture/icons';
import './ShapeEditorControl.scss';
import { primaryColourMain } from 'app/agriculture/material';
import { useCallback } from 'react';
import { ShapeEditorActorContext } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorMachine';
import { InfraType } from 'app/agriculture/mutations/types/entityConfig';
import { ShapeEditorHelpText } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorControl/ShapeEditorHelpText';

interface ShapeEditorControlProps {
    infraType: InfraType;
}

export function ShapeEditorControl({ infraType }: ShapeEditorControlProps) {
    const { isSnappingEnabled, mode, points, selectedPointIndex } =
        ShapeEditorActorContext.useSelector((state) => state.context);
    const isEditing = ShapeEditorActorContext.useSelector((state) => state.matches('Editing'));
    const actor = ShapeEditorActorContext.useActorRef();

    const handleSnappingChange = useCallback(
        (checked: boolean) => {
            actor.send({ type: 'changeIsSnappingEnabled', isEnabled: checked });
        },
        [actor],
    );

    const canClose =
        (mode === 'Polygon' && points.length > 2) || (mode === 'LineString' && points.length > 1);

    const canRemove =
        isEditing &&
        selectedPointIndex !== -1 &&
        ((mode === 'Polygon' && points.length > 4) || (mode === 'LineString' && points.length > 2));

    const isEndpoint = selectedPointIndex === 0 || selectedPointIndex === points.length - 1;
    const canExtend = isEditing && mode === 'LineString' && isEndpoint;

    return (
        <div className="ShapeEditorControl">
            <div className="ShapeEditorControl__content">
                <div className="ShapeEditorControl__title">
                    <EditIcon color={primaryColourMain} />
                    <h4>{`${infraType} Drawing Tool`}</h4>
                </div>
                <div className="ShapeEditorControl__help">
                    <p>
                        <ShapeEditorHelpText infraType={infraType} canRemovePoint={canRemove} />
                    </p>
                </div>
                <div className="ShapeEditorControl__snapping">
                    <AgCheckbox
                        id="snapping-checkbox"
                        isChecked={isSnappingEnabled}
                        onChange={handleSnappingChange}
                        label="Enable Snapping"
                    />
                </div>
            </div>
            <div className="ShapeEditorControl__controls">
                {isEditing ? (
                    <>
                        {canExtend && (
                            <Button
                                className="ShapeEditorControl__button"
                                text="Add Points"
                                variant="textonly"
                                onClick={() => {
                                    actor.send({ type: 'editEnterCreateModeFromEndpoint' });
                                }}
                                startIcon={<PlusIcon />}
                            />
                        )}
                        <Button
                            className="ShapeEditorControl__button"
                            text="Remove Point"
                            variant="textonly"
                            onClick={() => {
                                actor.send({ type: 'editDeleteSelectedPoint' });
                            }}
                            disabled={!canRemove}
                            startIcon={<MinusIcon />}
                        />
                    </>
                ) : (
                    <>
                        <Button
                            className="ShapeEditorControl__button"
                            text="Undo Last Point"
                            variant="textonly"
                            onClick={() => {
                                actor.send({ type: 'createUndoLastPoint' });
                            }}
                            disabled={points.length === 0}
                            startIcon={<MinusIcon />}
                        />
                        <Button
                            className="ShapeEditorControl__button"
                            text={`${mode === 'Polygon' ? 'Close' : 'Finish'} ${infraType}`}
                            variant="textonly"
                            disabled={!canClose}
                            onClick={() => {
                                actor.send({ type: 'createClickEndIcon' });
                            }}
                            startIcon={<CheckIcon />}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
